import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles, Box, BodyText, Card, TextField } from 'vopay';
import { Button, CircleButton } from 'UI';
import { Dialog } from '@/providers/Dialog.provider';
import styles from './AddSendingDomainDialog.module.scss';

const cn = withStyles(styles);

// A domain only — at least two dot-separated labels, no leading/trailing hyphen per label — so an
// email address (which the helper text explicitly warns against) fails validation.
const DOMAIN_REGEX = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.(?!-)[A-Za-z0-9-]{1,63}(?<!-))+$/;

type AddSendingDomainDialogProps = {
  onConfirm: () => void;
  onCancel: () => void;
  onSubmit: (domain: string) => Promise<void>;
};

// Opened via useDialog().open() with { bare: true }, so this component owns its full card chrome —
// DialogProvider supplies only centering/stacking/overlay, no default card.
export default function AddSendingDomainDialog({ onConfirm, onCancel, onSubmit }: AddSendingDomainDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.EmailDomain.AddDialog' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const domain = value.trim();
  const validationError =
    domain && !DOMAIN_REGEX.test(domain)
      ? t('invalidDomain', { defaultValue: 'Enter a valid domain, like yourcompany.com.' })
      : null;
  const error = submitError ?? (touched ? validationError : null);
  const canSubmit = domain !== '' && !validationError && !isSubmitting;

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setValue(event.currentTarget.value);
    setSubmitError(null);
  }

  async function handleSubmit() {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      await onSubmit(domain);
      onConfirm();
    } catch (submitException) {
      const fallbackError = t('submitError', { defaultValue: 'Something went wrong. Please try again.' });
      setSubmitError(
        submitException instanceof Error && submitException.message ? submitException.message : fallbackError,
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Card className={cn('card')} hoverEffect={false}>
      <div className={cn('header')}>
        <Dialog.Title level="4" className={cn('title')}>
          {t('title', { defaultValue: 'Add Sending Domain' })}
        </Dialog.Title>

        <CircleButton icon="close" size="large" circleFill aria-label={tGlobal('Buttons.close')} onClick={onCancel} />
      </div>

      <div className={cn('body')}>
        <Box flx="col" gapSize="8px">
          <Box flx="row" gapSize="2px" ai="center">
            <BodyText>{t('domainLabel', { defaultValue: 'Domain' })}</BodyText>
            <span className={cn('required')}>*</span>
          </Box>

          <TextField
            name="domain"
            variant="outlineShadow"
            value={value}
            placeholder={t('domainPlaceholder', { defaultValue: 'yourcompany.com' })}
            error={error}
            onChange={handleChange}
            onBlur={() => {
              setTouched(true);
            }}
            autoFocus
          />

          <BodyText size="sm" className={cn('helperText')}>
            {t('helperText', { defaultValue: 'Enter the domain only, not an email address.' })}
          </BodyText>
        </Box>
      </div>

      <div className={cn('footer')}>
        <Button colorScheme="primary" className={cn('submit')} disabled={!canSubmit} onClick={handleSubmit}>
          {isSubmitting
            ? t('submittingButton', { defaultValue: 'Adding...' })
            : t('submitButton', { defaultValue: 'Add domain' })}
        </Button>
      </div>
    </Card>
  );
}
